import PropTypes from "prop-types";
import * as Yup from "yup";
import { openSuccessDialog } from "../../../../../utils/ui-components/pop-ups/SuccessDialog";
import { Form, Formik } from "formik";
import {
  Accordion,
  useTheme,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  useMediaQuery,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import { gridSpacing, IMAGE_SIZE, IMAGE_SUPPORTED_FORMATS } from "../../../../../store/constants";
import TextField from "../../../../../utils/ui-components/FormsUI/TextField";
import React, { useEffect, useState } from "react";
import Select from "../../../../../utils/ui-components/FormsUI/Select";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RateCardService } from "../../../../../assets/_services/rate-card-service";
// import { convertFileToBase64 } from "../../../../utils/utils";
import { UserService } from "../../../../../assets/_services/user-service";
import { UserAddValues } from "../approve-user-list";
import PendingUserInfoCard from "./pending-user-info-card";
import SwitchWrapper from "../../../../../utils/ui-components/FormsUI/Switch";
import CheckboxWrapper from "../../../../../utils/ui-components/FormsUI/Checkbox";
import RadioGroupWrapper from "../../../../../utils/ui-components/FormsUI/RadioGroup";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

ApproveUser.propTypes = {
  initialItem: PropTypes.object.isRequired,
  setOpen: PropTypes.func.isRequired,
  fetchData: PropTypes.func,
  initialData: PropTypes.array,
};

const ACCOUNT_TYPES = [
  {
    value: 1,
    label: "Prepaid",
  },
  {
    value: 2,
    label: "Postpaid",
  },
];

const INDUSTRY_TYPES = [
  {
    value: 1,
    label: "Construction",
  },
  {
    value: 2,
    label: "Retail",
  },
  {
    value: 3,
    label: "Transportation",
  },
  {
    value: 4,
    label: "Financial Service",
  },
  {
    value: 5,
    label: "Agriculture",
  },
  {
    value: 6,
    label: "Education",
  },
  {
    value: 7,
    label: "Technology",
  },
  {
    value: 8,
    label: "Health Care",
  },
];

const operatorLabels: Record<number, string> = {
  1: "Robi",
  2: "Teletalk",
  3: "Banglalink",
  4: "Grameenphone",
};

function ApproveUser({ initialItem, setOpen, fetchData }: any) {
  const theme: any = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const matchDownMD = useMediaQuery(theme.breakpoints.down("lg"));
  const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));
  const [defaultOperator, setDefaultOperator] = useState<
    {
      value: number | string;
      label: string;
    }[]
  >(
    Object.entries(initialItem.smsOperatorConfguraion)
      .filter(([_, value]) => typeof value === "object" && value !== null && "isUse" in value && (value as any).isUse === true)
      .map(([_, value]) => {
        const operator = value as { systemOperatorId: number };
        return {
          value: operator.systemOperatorId,
          label: operatorLabels[operator.systemOperatorId],
        };
      }),
  );
  const [passwordShowOperator, setPasswordShowOperator]: any = React.useState({
    1: { showPassword: false, showConfirmPassword: false },
    2: { showPassword: false, showConfirmPassword: false },
    3: { showPassword: false, showConfirmPassword: false },
    4: { showPassword: false, showConfirmPassword: false },
  });
  const [rateCards, setRateCards] = useState([]);
  const [dataRateCards, setDataRateCards] = useState([]);

  useEffect(() => {
    fetchAllRateCardList();
  }, []);

  const fetchAllRateCardList = () => {
    RateCardService.getRateCardListWithoutPagination().then((response) => {
      if (response.isSuccess) {
        setRateCards(response.data.data.map((item: any) => ({ value: item.id, label: item.rateCardName })));
      }
    });

    RateCardService.getDigitalRateCardListWithoutPagination().then((response) => {
      if (response.isSuccess) {
        setDataRateCards(response.data.data.map((item: any) => ({ value: item.id, label: item.rateCardName })));
      }
    });
  };

  const FORM_VALIDATION = Yup.object().shape({
    profileImage: Yup.mixed()
      .nullable()
      .notRequired()
      .test("FILE_SIZE", "Uploaded file is too big.", ({ file: value }: any) => !value || (value && value?.size <= IMAGE_SIZE))
      .test(
        "FILE_FORMAT",
        "Uploaded file has unsupported format.",
        ({ file: value }: any) => !value || (value && IMAGE_SUPPORTED_FORMATS.includes(`${value?.type},`)),
      ),
    representativeName: Yup.string().required("Representative name is required"),
    email: Yup.string().email("Must be a valid email").max(255).required("Email is required"),
    companyName: Yup.string().required("Representative name is required"),
    companyContact: Yup.string()
      .matches(/^((880|0)?(1){1}[3456789]{1}(\d){8},?)+$/, "Invalid mobile numbers pattern detected")
      .required("Please enter company contact number"),
    username: Yup.string()
      .required("Username is required")
      .matches(/^[a-zA-Z0-9]*$/, "Username can only contain letters and numbers"),
    industryType: Yup.number().required("You must make a selection"),
    accountType: Yup.number().required("You must make a selection"),
    rateCardId: Yup.string().required("You must select a rate card"),
    dataRateCardId: Yup.string().when("enableRewardCampaignCreation", {
      is: (val: any) => val,
      then: (schema: any) => schema.required("Select data reward rate card"),
      otherwise: (schema: any) => schema.notRequired(),
    }),
    isShowRateCard: Yup.boolean().notRequired(),
    enableUserCreation: Yup.boolean().notRequired(),
    enableApiCreation: Yup.boolean().notRequired(),
    smsOperatorConfguraion: Yup.object()
      .shape({
        defaultOperator: Yup.string().required("Select default operator"),
        all: Yup.boolean().notRequired(),
        robi: Yup.object().shape({
          isUse: Yup.boolean().notRequired(),
          userName: Yup.string()
            // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers")
            .when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Robi username required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
          tps: Yup.number()
            .when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Robi TPS required"),
              otherwise: (schema: any) => schema.notRequired(),
            })
            .typeError("Allow only numbers")
            .integer("TPS must be an integer")
            .min(1, "TPS must be greater than or equal to 0"),
          password: Yup.string()
            .max(255)
            .when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Robi password required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
        }),
        tl: Yup.object().shape({
          isUse: Yup.boolean().notRequired(),
          userName: Yup.string().when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Teletalk username required"),
            otherwise: (schema: any) => schema.notRequired(),
          }),
          // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
          tps: Yup.number()
            .when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Teletalk TPS required"),
              otherwise: (schema: any) => schema.notRequired(),
            })
            .typeError("Allow only numbers")
            .integer("TPS must be an integer")
            .min(1, "TPS must be greater than or equal to 0"),
          acode: Yup.string().when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Teletalk acode required"),
            otherwise: (schema: any) => schema.notRequired(),
          }),
          password: Yup.string()
            .max(255)
            .when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Teletalk password required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
        }),
        bl: Yup.object().shape({
          isUse: Yup.boolean().notRequired(),
          userName: Yup.string().when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Banglalink username required"),
            otherwise: (schema: any) => schema.notRequired(),
          }),
          // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
          tps: Yup.number()
            .when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Banglalink TPS required"),
              otherwise: (schema: any) => schema.notRequired(),
            })
            .typeError("Allow only numbers")
            .integer("TPS must be an integer")
            .min(1, "TPS must be greater than or equal to 0"),
          password: Yup.string()
            .max(255)
            .when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Banglalink password required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
          billmsisdn: Yup.string().when("isUse", {
            is: (val: any) => val,
            then: (schema: any) =>
              schema.matches(/^((880)(\d){10},?)+$/, "Invalid numbers pattern detected").required("Banglalink billmsisdn required"),
            otherwise: (schema: any) => schema.notRequired(),
          }),
        }),
        gp: Yup.object().shape({
          isUse: Yup.boolean().notRequired(),
          userName: Yup.string().when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Grameenphone username required"),
            otherwise: (schema: any) => schema.notRequired(),
          }),
          // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
          tps: Yup.number()
            .when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Grameenphone TPS required"),
              otherwise: (schema: any) => schema.notRequired(),
            })
            .typeError("Allow only numbers")
            .integer("TPS must be an integer")
            .min(1, "TPS must be greater than or equal to 0"),
          password: Yup.string()
            .max(255)
            .when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Grameenphone password required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
          billmsisdn: Yup.string().when("isUse", {
            is: (val: any) => val,
            then: (schema: any) =>
              schema.matches(/^((0)(\d){10},?)+$/, "Invalid numbers pattern detected").required("Grameenphone billmsisdn required"),
            otherwise: (schema: any) => schema.notRequired(),
          }),
        }),
      })
      .test("valid", "valid", function (this: Yup.TestContext) {
        const { smsOperatorConfguraion } = this.parent;
        const { robi, tl, bl, gp } = smsOperatorConfguraion;
        const isAnyUseTrue = [robi, tl, bl, gp].some((item) => item.isUse === true);
        return isAnyUseTrue;
      }),
    rewardOperatorConfguraion: Yup.object().when("enableRewardCampaignCreation", {
      is: (val: any) => val,
      then: (schema: any) =>
        schema
          .shape({
            all: Yup.boolean().required("Required"),
            robi: Yup.object().shape({
              isUse: Yup.boolean().required("Required"),
              userName: Yup.string().when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Username is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
              // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
              password: Yup.string()
                .max(255)
                .when("isUse", {
                  is: (val: any) => val,
                  then: (schema: any) => schema.required("Password is required"),
                  otherwise: (schema: any) => schema.notRequired(),
                }),
            }),
            tl: Yup.object().shape({
              isUse: Yup.boolean().required("Required"),
              userName: Yup.string().when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Username is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
              // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
              password: Yup.string()
                .max(255)
                .when("isUse", {
                  is: (val: any) => val,
                  then: (schema: any) => schema.required("Password is required"),
                  otherwise: (schema: any) => schema.notRequired(),
                }),
            }),
            bl: Yup.object().shape({
              isUse: Yup.boolean().required("Required"),
              userName: Yup.string().when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Username is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
              // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
              password: Yup.string()
                .max(255)
                .when("isUse", {
                  is: (val: any) => val,
                  then: (schema: any) => schema.required("Password is required"),
                  otherwise: (schema: any) => schema.notRequired(),
                }),
              clientId: Yup.string().when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Client ID is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
              grantType: Yup.string().when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Grant type is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
              scope: Yup.string().when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Scope is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
              clientSecret: Yup.string().when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Client secret is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
              senderMsisdn: Yup.string()
                .matches(/^((880|0)?(1){1}[3456789]{1}(\d){8},?)+$/, "Invalid mobile numbers pattern detected")
                .when("isUse", {
                  is: (val: any) => val,
                  then: (schema: any) => schema.required("Sender Msisdn is required"),
                  otherwise: (schema: any) => schema.notRequired(),
                }),
            }),
            gp: Yup.object().shape({
              isUse: Yup.boolean().required("Required"),
              clientId: Yup.string().when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Client ID is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
              grantType: Yup.string().when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Grant type is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
              clientSecret: Yup.string().when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Client secret is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
            }),
          })
          .test("valid", "valid", function (this: Yup.TestContext) {
            const { rewardOperatorConfguraion } = this.parent;
            const { robi, tl, bl, gp } = rewardOperatorConfguraion;
            const isAnyUseTrue = [robi, tl, bl, gp].some((item) => item.isUse === true);
            return isAnyUseTrue;
          }),
      otherwise: (schema: any) => schema.notRequired(),
    }),
  });

  const approveUser = (itemData: UserAddValues, isApprove: any, setSubmitting: (isSubmitting: boolean) => void) => {
    setIsLoading(true);
    let formData = {
      userId: itemData.id,
      rateCardId: itemData.rateCardId,
      dataRateCardId: itemData?.enableRewardCampaignCreation ? itemData?.dataRateCardId : "",
      isApproved: isApprove,
      isAbleToCreateChildUser: itemData?.enableUserCreation ? 1 : 0,
      isApiUser: itemData?.enableApiCreation ? 1 : 0,
      isRateCardShow: itemData?.isShowRateCard ? 1 : 0,
      isReportDownload: itemData?.enableReportDownload ? 1 : 0,
      isNumberListHashed: itemData?.isCampaignNumberHash ? 1 : 0,
      isDataRewardAvailable: itemData?.enableRewardCampaignCreation ? 1 : 0,
      isAllOperatorsAllowed: itemData?.smsOperatorConfguraion.all ? 1 : 0,
      isAllDataOperatorAllowed: itemData?.rewardOperatorConfguraion.all ? 1 : 0,
      defaultOperatorId: Number(itemData?.smsOperatorConfguraion.defaultOperator),
      systemOperatorConfigurations: [
        itemData.smsOperatorConfguraion.robi.isUse && {
          systemOperatorId: itemData.smsOperatorConfguraion.robi.systemOperatorId,
          userName: itemData.smsOperatorConfguraion.robi.userName,
          password: itemData.smsOperatorConfguraion.robi.password,
          tps: itemData.smsOperatorConfguraion.robi.tps,
        },
        itemData.smsOperatorConfguraion.tl.isUse && {
          systemOperatorId: itemData.smsOperatorConfguraion.tl.systemOperatorId,
          userName: itemData.smsOperatorConfguraion.tl.userName,
          password: itemData.smsOperatorConfguraion.tl.password,
          acode: itemData.smsOperatorConfguraion.tl.acode,
          tps: itemData.smsOperatorConfguraion.tl.tps,
        },
        itemData.smsOperatorConfguraion.bl.isUse && {
          systemOperatorId: itemData.smsOperatorConfguraion.bl.systemOperatorId,
          userName: itemData.smsOperatorConfguraion.bl.userName,
          password: itemData.smsOperatorConfguraion.bl.password,
          tps: itemData.smsOperatorConfguraion.bl.tps,
          billmsisdn: itemData.smsOperatorConfguraion.bl.billmsisdn,
        },
        itemData.smsOperatorConfguraion.gp.isUse && {
          systemOperatorId: itemData.smsOperatorConfguraion.gp.systemOperatorId,
          userName: itemData.smsOperatorConfguraion.gp.userName,
          password: itemData.smsOperatorConfguraion.gp.password,
          tps: itemData.smsOperatorConfguraion.gp.tps,
          billmsisdn: itemData.smsOperatorConfguraion.gp.billmsisdn,
        },
      ].filter((item) => typeof item !== "boolean"),
      dataSystemOperatorConfigurations: itemData?.enableRewardCampaignCreation
        ? [
            itemData?.rewardOperatorConfguraion.robi.isUse && {
              systemOperatorId: itemData?.rewardOperatorConfguraion.robi.systemOperatorId,
              userName: itemData?.rewardOperatorConfguraion.robi.userName,
              password: itemData?.rewardOperatorConfguraion.robi.password,
            },
            itemData?.rewardOperatorConfguraion.tl.isUse && {
              systemOperatorId: itemData?.rewardOperatorConfguraion.tl.systemOperatorId,
              userName: itemData?.rewardOperatorConfguraion.tl.userName,
              password: itemData?.rewardOperatorConfguraion.tl.password,
            },
            itemData?.rewardOperatorConfguraion.bl.isUse && {
              systemOperatorId: itemData?.rewardOperatorConfguraion.bl.systemOperatorId,
              userName: itemData?.rewardOperatorConfguraion.bl.userName,
              password: itemData?.rewardOperatorConfguraion.bl.password,
              clientId: itemData?.rewardOperatorConfguraion.bl.clientId,
              grantType: itemData?.rewardOperatorConfguraion.bl.grantType,
              scope: itemData?.rewardOperatorConfguraion.bl.scope,
              clientSecret: itemData?.rewardOperatorConfguraion.bl.clientSecret,
              senderMsisdn: itemData?.rewardOperatorConfguraion.bl.senderMsisdn,
            },
            itemData?.rewardOperatorConfguraion.gp.isUse && {
              systemOperatorId: itemData?.rewardOperatorConfguraion.gp.systemOperatorId,
              clientId: itemData?.rewardOperatorConfguraion.gp.clientId,
              clientSecret: itemData?.rewardOperatorConfguraion.gp.clientSecret,
              grantType: itemData?.rewardOperatorConfguraion.gp.grantType,
            },
          ].filter((item) => typeof item !== "boolean")
        : [],
    };
    UserService.approveOrRejectOwner(formData).then((response) => {
      if (response.isSuccess) {
        openSuccessDialog("Success", response.data.comment);
        setOpen(false);
        fetchData();
        setIsLoading(false);
      } else {
        setSubmitting(false);
        setIsLoading(false);
      }
    });
  };

  const rejectUser = (itemData: UserAddValues, setSubmitting: (isSubmitting: boolean) => void) => {
    setIsLoading(true);

    let formData = {
      userId: itemData.id,
      isApproved: 2,
    };
    UserService.approveOrRejectOwner(formData).then((response) => {
      if (response.isSuccess) {
        openSuccessDialog("Success", response.data.comment);
        setOpen(false);
        fetchData();
        setIsLoading(false);
      } else {
        setSubmitting(false);
        setIsLoading(false);
      }
    });
  };

  const handleClickShowPasswordOperator = (id: number) => {
    setPasswordShowOperator((prevState: any) => ({
      ...prevState,
      [id]: {
        ...prevState[id],
        showPassword: !prevState[id].showPassword,
      },
    }));
  };

  const handleClickShowConfirmPasswordOperator = (id: number) => {
    setPasswordShowOperator((prevState: any) => ({
      ...prevState,
      [id]: {
        ...prevState[id],
        showConfirmPassword: !prevState[id].showConfirmPassword,
      },
    }));
  };

  return (
    <Formik
      initialValues={{
        ...initialItem,
      }}
      validationSchema={FORM_VALIDATION}
      onSubmit={(values: UserAddValues, { setSubmitting }) => {
        let isApprove = 1;
        approveUser(values, isApprove, setSubmitting);
      }}
    >
      {({ values, dirty, isSubmitting, isValid, setFieldValue, setSubmitting }) => (
        <Form>
          <Grid container columnSpacing={gridSpacing}>
            <Grid item xs={12} lg={12} md={12}>
              <PendingUserInfoCard color="primary" data={initialItem} industry={INDUSTRY_TYPES} accounts={ACCOUNT_TYPES} />
            </Grid>
            <Grid item xs={6}>
              <Grid container>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} textAlign="left">
                    <Typography variant="caption" fontWeight={"bold"}>
                      User Access
                    </Typography>
                  </Divider>
                  <Grid item xs={12}>
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <Tooltip title="fire">
                        <HelpOutlineIcon fontSize="small" color="disabled" />
                      </Tooltip>
                      <SwitchWrapper
                        name="isShowRateCard"
                        label={values.isShowRateCard ? "Show Rate Card" : "Hide Rate Card"}
                        style={{ co: "green" }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <Tooltip title="fire">
                        <HelpOutlineIcon fontSize="small" color="disabled" />
                      </Tooltip>
                      <SwitchWrapper
                        name="enableUserCreation"
                        label={values.enableUserCreation ? "Enable User Creation" : "Disable User Creation"}
                        style={{ co: "green" }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <Tooltip title="fire">
                        <HelpOutlineIcon fontSize="small" color="disabled" />
                      </Tooltip>
                      <SwitchWrapper
                        name="enableApiCreation"
                        label={values.enableApiCreation ? "Enable API Campaign" : "Disable API Campaign"}
                        style={{ co: "green" }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item lg={12}>
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <Tooltip title="fire">
                        <HelpOutlineIcon fontSize="small" color="disabled" />
                      </Tooltip>
                      <SwitchWrapper
                        name="enableRewardCampaignCreation"
                        label={values.enableRewardCampaignCreation ? "Enable Data Reward Campaign" : "Disable Data Reward Campaign"}
                        style={{ co: "green" }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <Tooltip title="fire">
                        <HelpOutlineIcon fontSize="small" color="disabled" />
                      </Tooltip>
                      <SwitchWrapper
                        name="enableReportDownload"
                        label={values.enableReportDownload ? "Enable Report Download" : "Disable Report Download"}
                        style={{ co: "green" }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <Tooltip title="fire">
                        <HelpOutlineIcon fontSize="small" color="disabled" />
                      </Tooltip>
                      <SwitchWrapper
                        name="isCampaignNumberHash"
                        label={values.isCampaignNumberHash ? "Hashed Campaign Numbers" : "Show Campaign Numbers"}
                        style={{ co: "green" }}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} textAlign="left">
                    <Typography variant="caption" fontWeight={"bold"}>
                      Rate Cards
                    </Typography>
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <Select label="Rate Card *" name="rateCardId" options={rateCards} customHandleChange={() => {}} />
                </Grid>
                {values.enableRewardCampaignCreation && (
                  <Grid item xs={12}>
                    <Select label="Data Reward Rate Card *" name="dataRateCardId" options={dataRateCards} customHandleChange={() => {}} />
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12} mt={2}>
              <Grid container spacing={1} justifyContent={"center"}>
                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#ffff" }} />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                      }}
                    >
                      <Typography fontWeight={"bold"} variant="h4" color={"#ffff"}>
                        SMS Operators Configuration
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={gridSpacing} justifyContent={"center"}>
                        <Grid item xs={12}>
                          <CheckboxWrapper
                            customHandleChange={(e: any) => {
                              if (e.target.checked) {
                                setFieldValue("smsOperatorConfguraion", {
                                  all: true,
                                  defaultOperator: "",
                                  robi: {
                                    ...values.smsOperatorConfguraion.robi,
                                    isUse: true,
                                  },
                                  tl: {
                                    ...values.smsOperatorConfguraion.tl,
                                    isUse: true,
                                  },
                                  bl: {
                                    ...values.smsOperatorConfguraion.bl,
                                    isUse: true,
                                  },
                                  gp: {
                                    ...values.smsOperatorConfguraion.gp,
                                    isUse: true,
                                  },
                                });
                                setDefaultOperator([
                                  { value: 1, label: operatorLabels[1] },
                                  { value: 2, label: operatorLabels[2] },
                                  { value: 3, label: operatorLabels[3] },
                                  { value: 4, label: operatorLabels[4] },
                                ]);
                              } else {
                                setFieldValue("smsOperatorConfguraion", {
                                  all: false,
                                  defaultOperator: "",
                                  robi: {
                                    ...values.smsOperatorConfguraion.robi,
                                    isUse: false,
                                  },
                                  tl: {
                                    ...values.smsOperatorConfguraion.tl,
                                    isUse: false,
                                  },
                                  bl: {
                                    ...values.smsOperatorConfguraion.bl,
                                    isUse: false,
                                  },
                                  gp: {
                                    ...values.smsOperatorConfguraion.gp,
                                    isUse: false,
                                  },
                                });
                                setDefaultOperator([]);
                              }
                            }}
                            legend={"All Operators"}
                            name={"smsOperatorConfguraion.all"}
                            sx={{ pr: 0 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <RadioGroupWrapper
                            required
                            name="smsOperatorConfguraion.defaultOperator"
                            label="SMS Default Operator"
                            radioGroup={defaultOperator}
                            disabled={false}
                          />
                        </Grid>

                        <Grid item lg={12} container columnSpacing={1}>
                          <Grid item lg={2} md={2} sm={3} xs={3}>
                            <CheckboxWrapper
                              customHandleChange={(e: any) => {
                                if (
                                  e?.target.checked &&
                                  values.smsOperatorConfguraion.tl.isUse &&
                                  values.smsOperatorConfguraion.bl.isUse &&
                                  values.smsOperatorConfguraion.gp.isUse
                                ) {
                                  setFieldValue("smsOperatorConfguraion", {
                                    ...values.smsOperatorConfguraion,
                                    all: true,
                                    robi: {
                                      ...values.smsOperatorConfguraion.robi,
                                      isUse: e?.target.checked,
                                    },
                                  });
                                  setDefaultOperator([...defaultOperator, { value: 1, label: operatorLabels[1] }]);
                                } else {
                                  setFieldValue("smsOperatorConfguraion", {
                                    ...values.smsOperatorConfguraion,
                                    defaultOperator:
                                      Number(values.smsOperatorConfguraion.defaultOperator) === 1
                                        ? ""
                                        : Number(values.smsOperatorConfguraion.defaultOperator),
                                    all: false,
                                    robi: {
                                      ...values.smsOperatorConfguraion.robi,
                                      isUse: e?.target.checked,
                                    },
                                  });
                                  setDefaultOperator(defaultOperator.filter((item) => item.value !== 1));
                                }

                                if (e?.target.checked) {
                                  setDefaultOperator(
                                    [...defaultOperator, { value: 1, label: operatorLabels[1] }].sort((a, b) => Number(a.value) - Number(b.value)),
                                  );
                                } else {
                                  setDefaultOperator(
                                    defaultOperator.filter((item) => item.value !== 1).sort((a, b) => Number(a.value) - Number(b.value)),
                                  );
                                }
                              }}
                              legend={"Robi"}
                              name={"smsOperatorConfguraion.robi.isUse"}
                              sx={{ pr: 0 }}
                            />
                          </Grid>
                          <Grid item lg={10} container spacing={1}>
                            <Grid item lg={2} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.robi.isUse}
                                type={"number"}
                                name="smsOperatorConfguraion.robi.tps"
                                label="TPS"
                              />
                            </Grid>
                            <Grid item lg={4} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.robi.isUse}
                                type={"text"}
                                name="smsOperatorConfguraion.robi.userName"
                                label="Username"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <PersonIcon />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item lg={4} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.robi.isUse}
                                type={passwordShowOperator[1]?.showPassword ? "text" : "password"}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <LockIcon />
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => handleClickShowPasswordOperator(1)}
                                        edge="end"
                                      >
                                        {passwordShowOperator[1]?.showPassword ? <VisibilityOff /> : <Visibility />}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                name="smsOperatorConfguraion.robi.password"
                                label="Password"
                                placeholder="Password"
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={12} container columnSpacing={1}>
                          <Grid item lg={2} md={2} sm={3} xs={3}>
                            <CheckboxWrapper
                              customHandleChange={(e: any) => {
                                if (
                                  e?.target.checked &&
                                  values.smsOperatorConfguraion.robi.isUse &&
                                  values.smsOperatorConfguraion.bl.isUse &&
                                  values.smsOperatorConfguraion.gp.isUse
                                ) {
                                  setFieldValue("smsOperatorConfguraion", {
                                    ...values.smsOperatorConfguraion,
                                    all: true,
                                    tl: {
                                      ...values.smsOperatorConfguraion.tl,
                                      isUse: e?.target.checked,
                                    },
                                  });
                                } else {
                                  setFieldValue("smsOperatorConfguraion", {
                                    ...values.smsOperatorConfguraion,
                                    defaultOperator:
                                      Number(values.smsOperatorConfguraion.defaultOperator) === 2
                                        ? ""
                                        : Number(values.smsOperatorConfguraion.defaultOperator),
                                    all: false,
                                    tl: {
                                      ...values.smsOperatorConfguraion.tl,
                                      isUse: e?.target.checked,
                                    },
                                  });
                                }
                                if (e?.target.checked) {
                                  setDefaultOperator(
                                    [...defaultOperator, { value: 2, label: operatorLabels[2] }].sort((a, b) => Number(a.value) - Number(b.value)),
                                  );
                                } else {
                                  setDefaultOperator(
                                    defaultOperator.filter((item) => item.value !== 2).sort((a, b) => Number(a.value) - Number(b.value)),
                                  );
                                }
                              }}
                              legend={"Teletalk"}
                              name={"smsOperatorConfguraion.tl.isUse"}
                              sx={{ pr: 0 }}
                            />
                          </Grid>
                          <Grid item lg={10} container spacing={1}>
                            <Grid item lg={2} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.tl.isUse}
                                type={"number"}
                                name="smsOperatorConfguraion.tl.tps"
                                label="TPS"
                              />
                            </Grid>
                            <Grid item lg={4} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.tl.isUse}
                                type={"text"}
                                name="smsOperatorConfguraion.tl.userName"
                                label="Username"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <PersonIcon />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item lg={4} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.tl.isUse}
                                type={passwordShowOperator[1]?.showPassword ? "text" : "password"}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <LockIcon />
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => handleClickShowPasswordOperator(1)}
                                        edge="end"
                                      >
                                        {passwordShowOperator[1]?.showPassword ? <VisibilityOff /> : <Visibility />}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                name="smsOperatorConfguraion.tl.password"
                                label="Password"
                                placeholder="Password"
                              />
                            </Grid>
                            <Grid item lg={2} md={10} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.tl.isUse}
                                type={"text"}
                                name="smsOperatorConfguraion.tl.acode"
                                label="Acode"
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={12} container columnSpacing={1}>
                          <Grid item lg={2} md={2} sm={3} xs={3}>
                            <CheckboxWrapper
                              customHandleChange={(e: any) => {
                                if (
                                  e?.target.checked &&
                                  values.smsOperatorConfguraion.tl.isUse &&
                                  values.smsOperatorConfguraion.robi.isUse &&
                                  values.smsOperatorConfguraion.gp.isUse
                                ) {
                                  setFieldValue("smsOperatorConfguraion", {
                                    ...values.smsOperatorConfguraion,
                                    all: true,
                                    bl: {
                                      ...values.smsOperatorConfguraion.bl,
                                      isUse: e?.target.checked,
                                    },
                                  });
                                } else {
                                  setFieldValue("smsOperatorConfguraion", {
                                    ...values.smsOperatorConfguraion,
                                    defaultOperator:
                                      Number(values.smsOperatorConfguraion.defaultOperator) === 3
                                        ? ""
                                        : Number(values.smsOperatorConfguraion.defaultOperator),
                                    all: false,
                                    bl: {
                                      ...values.smsOperatorConfguraion.bl,
                                      isUse: e?.target.checked,
                                    },
                                  });
                                }
                                if (e?.target.checked) {
                                  setDefaultOperator(
                                    [...defaultOperator, { value: 3, label: operatorLabels[3] }].sort((a, b) => Number(a.value) - Number(b.value)),
                                  );
                                } else {
                                  setDefaultOperator(
                                    defaultOperator.filter((item) => item.value !== 3).sort((a, b) => Number(a.value) - Number(b.value)),
                                  );
                                }
                              }}
                              legend={"Banglalink"}
                              name={"smsOperatorConfguraion.bl.isUse"}
                              sx={{ pr: 0 }}
                            />
                          </Grid>
                          <Grid item lg={10} container spacing={1}>
                            <Grid item lg={2} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.bl.isUse}
                                type={"number"}
                                name="smsOperatorConfguraion.bl.tps"
                                label="TPS"
                              />
                            </Grid>
                            <Grid item lg={3.5} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.bl.isUse}
                                type={"text"}
                                name="smsOperatorConfguraion.bl.userName"
                                label="Username"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <PersonIcon />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item lg={3.5} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.bl.isUse}
                                type={passwordShowOperator[1]?.showPassword ? "text" : "password"}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <LockIcon />
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => handleClickShowPasswordOperator(1)}
                                        edge="end"
                                      >
                                        {passwordShowOperator[1]?.showPassword ? <VisibilityOff /> : <Visibility />}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                name="smsOperatorConfguraion.bl.password"
                                label="Password"
                                placeholder="Password"
                              />
                            </Grid>
                            <Grid item lg={3} md={10} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.bl.isUse}
                                type="tel"
                                name="smsOperatorConfguraion.bl.billmsisdn"
                                label="Bill"
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={12} container columnSpacing={1}>
                          <Grid item lg={2} md={2} sm={3} xs={3}>
                            <CheckboxWrapper
                              customHandleChange={(e: any) => {
                                if (
                                  e?.target.checked &&
                                  values.smsOperatorConfguraion.tl.isUse &&
                                  values.smsOperatorConfguraion.bl.isUse &&
                                  values.smsOperatorConfguraion.robi.isUse
                                ) {
                                  setFieldValue("smsOperatorConfguraion", {
                                    ...values.smsOperatorConfguraion,
                                    all: true,
                                    gp: {
                                      ...values.smsOperatorConfguraion.gp,
                                      isUse: e?.target.checked,
                                    },
                                  });
                                } else {
                                  setFieldValue("smsOperatorConfguraion", {
                                    ...values.smsOperatorConfguraion,
                                    defaultOperator:
                                      Number(values.smsOperatorConfguraion.defaultOperator) === 4
                                        ? ""
                                        : Number(values.smsOperatorConfguraion.defaultOperator),
                                    all: false,
                                    gp: {
                                      ...values.smsOperatorConfguraion.gp,
                                      isUse: e?.target.checked,
                                    },
                                  });
                                }
                                if (e?.target.checked) {
                                  setDefaultOperator(
                                    [...defaultOperator, { value: 4, label: operatorLabels[4] }].sort((a, b) => Number(a.value) - Number(b.value)),
                                  );
                                } else {
                                  setDefaultOperator(
                                    defaultOperator.filter((item) => item.value !== 4).sort((a, b) => Number(a.value) - Number(b.value)),
                                  );
                                }
                              }}
                              legend={"Grameenphone"}
                              name={"smsOperatorConfguraion.gp.isUse"}
                              sx={{ pr: 0 }}
                            />
                          </Grid>
                          <Grid item lg={10} container spacing={1}>
                            <Grid item lg={2} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.gp.isUse}
                                type={"number"}
                                name="smsOperatorConfguraion.gp.tps"
                                label="TPS"
                              />
                            </Grid>
                            <Grid item lg={3.5} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.gp.isUse}
                                type={"text"}
                                name="smsOperatorConfguraion.gp.userName"
                                label="Username"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <PersonIcon />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item lg={3.5} md={5} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.gp.isUse}
                                type={passwordShowOperator[1]?.showPassword ? "text" : "password"}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <LockIcon />
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => handleClickShowPasswordOperator(1)}
                                        edge="end"
                                      >
                                        {passwordShowOperator[1]?.showPassword ? <VisibilityOff /> : <Visibility />}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                name="smsOperatorConfguraion.gp.password"
                                label="Password"
                                placeholder="Password"
                              />
                            </Grid>
                            <Grid item lg={3} md={10} sm={8} xs={9}>
                              <TextField
                                required={values.smsOperatorConfguraion.gp.isUse}
                                type="tel"
                                name="smsOperatorConfguraion.gp.billmsisdn"
                                label="Bill"
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          {!values.smsOperatorConfguraion.bl.isUse &&
                            !values.smsOperatorConfguraion.gp.isUse &&
                            !values.smsOperatorConfguraion.robi.isUse &&
                            !values.smsOperatorConfguraion.tl.isUse && (
                              <Typography variant="caption" color={"error"}>
                                At least one sms reward operator required
                              </Typography>
                            )}
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                {values.enableRewardCampaignCreation && (
                  <Grid item xs={12} sx={{ mt: 0.1 }}>
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#ffff" }} />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                        }}
                      >
                        <Typography fontWeight={"bold"} variant="h4" color={"#ffff"}>
                          Data Reward Operators Configuration
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={gridSpacing} justifyContent={"center"}>
                          <Grid item xs={12}>
                            <CheckboxWrapper
                              customHandleChange={(e: any) => {
                                if (e.target.checked) {
                                  setFieldValue("rewardOperatorConfguraion", {
                                    all: true,
                                    robi: {
                                      ...values.rewardOperatorConfguraion.robi,
                                      isUse: true,
                                    },
                                    tl: {
                                      ...values.rewardOperatorConfguraion.tl,
                                      isUse: true,
                                    },
                                    bl: {
                                      ...values.rewardOperatorConfguraion.bl,
                                      isUse: true,
                                    },
                                    gp: {
                                      ...values.rewardOperatorConfguraion.gp,
                                      isUse: true,
                                    },
                                  });
                                } else {
                                  setFieldValue("rewardOperatorConfguraion", {
                                    all: false,
                                    robi: {
                                      ...values.rewardOperatorConfguraion.robi,
                                      isUse: false,
                                    },
                                    tl: {
                                      ...values.rewardOperatorConfguraion.tl,
                                      isUse: false,
                                    },
                                    bl: {
                                      ...values.rewardOperatorConfguraion.bl,
                                      isUse: false,
                                    },
                                    gp: {
                                      ...values.rewardOperatorConfguraion.gp,
                                      isUse: false,
                                    },
                                  });
                                }
                              }}
                              legend={"All Operators"}
                              name={"rewardOperatorConfguraion.all"}
                              sx={{ pr: 0 }}
                            />
                          </Grid>

                          <Grid item lg={12} container columnSpacing={1}>
                            <Grid item lg={2} md={2} sm={3} xs={3}>
                              <CheckboxWrapper
                                customHandleChange={(e: any) => {
                                  if (
                                    e?.target.checked &&
                                    values.rewardOperatorConfguraion.tl.isUse &&
                                    values.rewardOperatorConfguraion.bl.isUse &&
                                    values.rewardOperatorConfguraion.gp.isUse
                                  ) {
                                    setFieldValue("rewardOperatorConfguraion", {
                                      ...values.rewardOperatorConfguraion,
                                      all: true,
                                      robi: {
                                        ...values.rewardOperatorConfguraion.robi,
                                        isUse: e?.target.checked,
                                      },
                                    });
                                  } else {
                                    setFieldValue("rewardOperatorConfguraion", {
                                      ...values.rewardOperatorConfguraion,
                                      all: false,
                                      robi: {
                                        ...values.rewardOperatorConfguraion.robi,
                                        isUse: e?.target.checked,
                                      },
                                    });
                                  }
                                }}
                                legend={"Robi"}
                                name={"rewardOperatorConfguraion.robi.isUse"}
                                sx={{ pr: 0 }}
                              />
                            </Grid>
                            <Grid item lg={10} container spacing={1}>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.robi.isUse}
                                  type={"text"}
                                  name="rewardOperatorConfguraion.robi.userName"
                                  label="Username"
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <PersonIcon />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.robi.isUse}
                                  type={passwordShowOperator[1]?.showPassword ? "text" : "password"}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <LockIcon />
                                      </InputAdornment>
                                    ),
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          onClick={() => handleClickShowPasswordOperator(1)}
                                          edge="end"
                                        >
                                          {passwordShowOperator[1]?.showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  name="rewardOperatorConfguraion.robi.password"
                                  label="Password"
                                  placeholder="Password"
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item lg={12} container columnSpacing={1}>
                            <Grid item lg={2} md={2} sm={3} xs={3}>
                              <CheckboxWrapper
                                customHandleChange={(e: any) => {
                                  if (
                                    e?.target.checked &&
                                    values.rewardOperatorConfguraion.robi.isUse &&
                                    values.rewardOperatorConfguraion.bl.isUse &&
                                    values.rewardOperatorConfguraion.gp.isUse
                                  ) {
                                    setFieldValue("rewardOperatorConfguraion", {
                                      ...values.rewardOperatorConfguraion,
                                      all: true,
                                      tl: {
                                        ...values.rewardOperatorConfguraion.tl,
                                        isUse: e?.target.checked,
                                      },
                                    });
                                  } else {
                                    setFieldValue("rewardOperatorConfguraion", {
                                      ...values.rewardOperatorConfguraion,
                                      all: false,
                                      tl: {
                                        ...values.rewardOperatorConfguraion.tl,
                                        isUse: e?.target.checked,
                                      },
                                    });
                                  }
                                }}
                                legend={"Teletalk"}
                                name={"rewardOperatorConfguraion.tl.isUse"}
                                sx={{ pr: 0 }}
                              />
                            </Grid>
                            <Grid item lg={10} container spacing={1}>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.tl.isUse}
                                  type={"text"}
                                  name="rewardOperatorConfguraion.tl.userName"
                                  label="Username"
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <PersonIcon />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.tl.isUse}
                                  type={passwordShowOperator[1]?.showPassword ? "text" : "password"}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <LockIcon />
                                      </InputAdornment>
                                    ),
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          onClick={() => handleClickShowPasswordOperator(1)}
                                          edge="end"
                                        >
                                          {passwordShowOperator[1]?.showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  name="rewardOperatorConfguraion.tl.password"
                                  label="Password"
                                  placeholder="Password"
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item lg={12} container columnSpacing={1}>
                            <Grid item lg={2} md={2} sm={3} xs={3}>
                              <CheckboxWrapper
                                customHandleChange={(e: any) => {
                                  if (
                                    e?.target.checked &&
                                    values.rewardOperatorConfguraion.tl.isUse &&
                                    values.rewardOperatorConfguraion.robi.isUse &&
                                    values.rewardOperatorConfguraion.gp.isUse
                                  ) {
                                    setFieldValue("rewardOperatorConfguraion", {
                                      ...values.rewardOperatorConfguraion,
                                      all: true,
                                      bl: {
                                        ...values.rewardOperatorConfguraion.bl,
                                        isUse: e?.target.checked,
                                      },
                                    });
                                  } else {
                                    setFieldValue("rewardOperatorConfguraion", {
                                      ...values.rewardOperatorConfguraion,
                                      all: false,
                                      bl: {
                                        ...values.rewardOperatorConfguraion.bl,
                                        isUse: e?.target.checked,
                                      },
                                    });
                                  }
                                }}
                                legend={"Banglalink"}
                                name={"rewardOperatorConfguraion.bl.isUse"}
                                sx={{ pr: 0 }}
                              />
                            </Grid>
                            <Grid item lg={10} container spacing={1}>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.bl.isUse}
                                  type={"text"}
                                  name="rewardOperatorConfguraion.bl.userName"
                                  label="Username"
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <PersonIcon />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.bl.isUse}
                                  type={passwordShowOperator[1]?.showPassword ? "text" : "password"}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <LockIcon />
                                      </InputAdornment>
                                    ),
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          onClick={() => handleClickShowPasswordOperator(1)}
                                          edge="end"
                                        >
                                          {passwordShowOperator[1]?.showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  name="rewardOperatorConfguraion.bl.password"
                                  label="Password"
                                  placeholder="Password"
                                />
                              </Grid>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.bl.isUse}
                                  type={"text"}
                                  name="rewardOperatorConfguraion.bl.clientId"
                                  label="Client Id"
                                />
                              </Grid>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.bl.isUse}
                                  type={"text"}
                                  name="rewardOperatorConfguraion.bl.grantType"
                                  label="Grant Type"
                                />
                              </Grid>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.bl.isUse}
                                  type={"text"}
                                  name="rewardOperatorConfguraion.bl.scope"
                                  label="Scope"
                                />
                              </Grid>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.bl.isUse}
                                  type={"text"}
                                  name="rewardOperatorConfguraion.bl.clientSecret"
                                  label="Client Secret"
                                />
                              </Grid>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.bl.isUse}
                                  type="tel"
                                  name="rewardOperatorConfguraion.bl.senderMsisdn"
                                  label="Sender Msisdn"
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item lg={12} container columnSpacing={1}>
                            <Grid item lg={2} md={2} sm={3} xs={3}>
                              <CheckboxWrapper
                                customHandleChange={(e: any) => {
                                  if (
                                    e?.target.checked &&
                                    values.rewardOperatorConfguraion.tl.isUse &&
                                    values.rewardOperatorConfguraion.bl.isUse &&
                                    values.rewardOperatorConfguraion.robi.isUse
                                  ) {
                                    setFieldValue("rewardOperatorConfguraion", {
                                      ...values.rewardOperatorConfguraion,
                                      all: true,
                                      gp: {
                                        ...values.rewardOperatorConfguraion.gp,
                                        isUse: e?.target.checked,
                                      },
                                    });
                                  } else {
                                    setFieldValue("rewardOperatorConfguraion", {
                                      ...values.rewardOperatorConfguraion,
                                      all: false,
                                      gp: {
                                        ...values.rewardOperatorConfguraion.gp,
                                        isUse: e?.target.checked,
                                      },
                                    });
                                  }
                                }}
                                legend={"Grameenphone"}
                                name={"rewardOperatorConfguraion.gp.isUse"}
                                sx={{ pr: 0 }}
                              />
                            </Grid>
                            <Grid item lg={10} container spacing={1}>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.gp.isUse}
                                  type={"text"}
                                  name="rewardOperatorConfguraion.gp.clientId"
                                  label="Client Id"
                                />
                              </Grid>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.gp.isUse}
                                  type={"text"}
                                  name="rewardOperatorConfguraion.gp.grantType"
                                  label="Grant Type"
                                />
                              </Grid>
                              <Grid item lg={4} md={5} sm={8} xs={9}>
                                <TextField
                                  required={values.rewardOperatorConfguraion.gp.isUse}
                                  type={"text"}
                                  name="rewardOperatorConfguraion.gp.clientSecret"
                                  label="Client Secret"
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item xs={12}>
                            {!values.rewardOperatorConfguraion.bl.isUse &&
                              !values.rewardOperatorConfguraion.gp.isUse &&
                              !values.rewardOperatorConfguraion.robi.isUse &&
                              !values.rewardOperatorConfguraion.tl.isUse && (
                                <Typography variant="caption" color={"error"}>
                                  At least one data reward operator required
                                </Typography>
                              )}
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  py: 2,
                }}
              >
                <Button
                  color="error"
                  variant="contained"
                  type="button"
                  sx={{ mr: 1 }}
                  onClick={() => {
                    // let isApprove = 2;
                    rejectUser(values, setSubmitting);
                  }}
                >
                  {isLoading && (
                    <CircularProgress
                      size={"20px"}
                      sx={{
                        mr: 1,
                        color: "gray",
                      }}
                    />
                  )}
                  Reject User
                </Button>
                <Button color="primary" variant="contained" type="submit" disabled={!dirty || isSubmitting || !isValid}>
                  {isLoading && (
                    <CircularProgress
                      size={"20px"}
                      sx={{
                        mr: 1,
                        color: "gray",
                      }}
                    />
                  )}
                  Approve User
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}

export default ApproveUser;
